/**
 * Owner redesign — property shell: floating teal sidebar with a sliding pill (desktop),
 * hamburger drawer with the same nav (phone), and the header (breadcrumb, title, switcher, bell, avatar).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Menu, Plus, Check, ChevronDown, X } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type { PropertyTabId } from "@/lib/propertyTabs";
import { NotificationBell } from "@/components/owner/NotificationBell";
import { ProfileDropdown } from "@/components/ProfileDropdown";
import { PropertySwitcher } from "@/components/PropertySwitcher";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessibleProperties } from "@/hooks/useAccessibleProperties";

export interface PropertySidebarNavProps {
  tabItems: { value: PropertyTabId; label: string }[];
  activeTab: PropertyTabId;
  onTabChange: (value: PropertyTabId) => void;
  propertyId: string;
  propertyName?: string;
  propertiesHref?: string;
  roleLabel?: string;
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

const TAB_ICON_PATHS: Partial<Record<PropertyTabId, string>> = {
  overview: "M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z",
  access: "M2 15.5a5.5 5.5 0 1 0 11 0a5.5 5.5 0 1 0-11 0M11.4 11.6L21 2M15.5 7.5l3 3L22 7l-3-3",
  schedule: "M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM16 2v4M8 2v4M3 10h18",
  "my-tasks": "M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  inventory:
    "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12",
  unit: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 22V12h6v10",
  rooms: "M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20M6 8v9",
  maintenance:
    "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01",
  reports: "M18 20V10M12 20V4M6 20v-6",
  settings: "M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6",
};

function TabIcon({ id, color }: { id: PropertyTabId; color: string }) {
  const d = TAB_ICON_PATHS[id] ?? TAB_ICON_PATHS.overview!;
  return (
    <span className="flex shrink-0" style={{ color, transition: "color 260ms ease" }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d={d} />
      </svg>
    </span>
  );
}

const ROW = 46;
const GAP = 2;
const DESIGN_SIDEBAR_H = 828; // 860px design frame minus 16px padding top and bottom

/** Scale for short screens: the whole sidebar shrinks together, so proportions stay exactly as designed. */
export function useSidebarScale() {
  const calc = () =>
    typeof window === "undefined" ? 1 : Math.max(0.7, Math.min(1, (window.innerHeight - 32) / DESIGN_SIDEBAR_H));
  const [scale, setScale] = useState(calc);
  useEffect(() => {
    const on = () => setScale(calc());
    window.addEventListener("resize", on);
    on();
    return () => window.removeEventListener("resize", on);
  }, []);
  return scale;
}
const PROW = 48;
const SPRING = "cubic-bezier(.45,1.45,.45,1)";

function userInitial(profile: { first_name?: string | null; full_name?: string | null } | null, email?: string | null) {
  if (profile?.first_name?.trim()) return profile.first_name.trim().charAt(0).toUpperCase();
  if (profile?.full_name?.trim()) return profile.full_name.trim().charAt(0).toUpperCase();
  return (email || "?").charAt(0).toUpperCase();
}

function userDisplayName(profile: { first_name?: string | null; last_name?: string | null; full_name?: string | null } | null, email?: string | null) {
  const full = profile?.full_name?.trim();
  if (full) return full;
  const both = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim();
  return both || email || "";
}

function Brand({ size = "desktop" }: { size?: "desktop" | "phone" }) {
  return (
    <div className="flex items-center gap-[11px]">
      <div
        className={cn(
          "flex items-center justify-center bg-white font-bold text-[#15776d]",
          size === "desktop" ? "h-9 w-9 rounded-[11px] text-[16px]" : "h-[34px] w-[34px] rounded-[11px] text-[15px]",
        )}
      >
        S
      </div>
      <div className={cn("font-bold tracking-[-0.3px]", size === "desktop" ? "text-[19px]" : "text-[18px]")}>Stravo</div>
    </div>
  );
}

function SlidingNav({
  tabItems,
  activeTab,
  onTabChange,
  size,
}: {
  tabItems: { value: PropertyTabId; label: string }[];
  activeTab: PropertyTabId;
  onTabChange: (value: PropertyTabId) => void;
  size: "desktop" | "phone";
}) {
  const idx = Math.max(0, tabItems.findIndex((t) => t.value === activeTab));
  const rowH = size === "desktop" ? ROW : PROW;
  const desktop = size === "desktop";
  const r = Math.min(23, rowH / 2);
  return (
    <nav className="relative flex flex-col" style={{ gap: GAP }} aria-label="Property sections">
      <div
        className="pointer-events-none absolute left-0 top-0"
        style={{
          background: "#f4f7f9",
          zIndex: 0,
          right: desktop ? -1 : 0,
          height: rowH,
          borderRadius: desktop ? `${r}px 0 0 ${r}px` : 16,
          transform: `translateY(${idx * (rowH + GAP)}px)`,
          transition: `transform 460ms ${SPRING}`,
        }}
      >
        {desktop ? (
          <>
            <div
              className="absolute right-0"
              style={{
                width: r + 2,
                height: r + 3,
                bottom: "calc(100% - 2px)",
                background: `radial-gradient(circle at 0 0, transparent ${r + 0.5}px, #f4f7f9 ${r + 1}px)`,
              }}
            />
            <div
              className="absolute right-0"
              style={{
                width: r + 2,
                height: r + 3,
                top: "calc(100% - 2px)",
                background: `radial-gradient(circle at 0 100%, transparent ${r + 0.5}px, #f4f7f9 ${r + 1}px)`,
              }}
            />
          </>
        ) : null}
      </div>
      {tabItems.map((tab) => {
        const on = tab.value === activeTab;
        return (
          <button
            key={tab.value}
            type="button"
            data-tour={`property-${tab.value}-tab`}
            onClick={() => onTabChange(tab.value)}
            aria-current={on ? "page" : undefined}
            className={cn(
              "relative z-[1] flex items-center gap-[13px] bg-transparent text-left outline-none focus-visible:outline-none",
              desktop ? "text-[15px]" : "text-[16px]",
              on ? "font-bold text-[#15776d]" : "font-medium text-[#e3f4f1]",
            )}
            style={{
              height: rowH,
              paddingRight: desktop ? 16 : 14,
              paddingLeft: on ? (desktop ? 20 : 18) : desktop ? 16 : 14,
              borderRadius: desktop ? `${r}px 0 0 ${r}px` : 16,
              transition: `color 260ms ease, padding-left 360ms ${SPRING}`,
            }}
          >
            <TabIcon id={tab.value} color={on ? "#24a89a" : "#9fd3cb"} />
            <span className="min-w-0 flex-1 truncate">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function UserChip({ size, roleLabel }: { size: "desktop" | "phone"; roleLabel: string }) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const name = userDisplayName(profile, user?.email);
  return (
    <button
      type="button"
      onClick={() => navigate("/account")}
      aria-label="My account"
      className="flex w-full items-center gap-[11px] rounded-[18px] bg-[#1f8a7f] p-3 text-left"
    >
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full bg-[#e2eeed] font-bold text-[#15776d]",
          size === "desktop" ? "h-9 w-9 text-[14px]" : "h-[34px] w-[34px] text-[13px]",
        )}
      >
        {userInitial(profile, user?.email)}
      </div>
      {size === "desktop" ? (
        <div className="flex min-w-0 flex-col gap-px">
          <div className="truncate text-[14px] font-bold">{name}</div>
          <div className="text-[12px] font-medium text-[#bfe3dd]">{roleLabel}</div>
        </div>
      ) : (
        <>
          <div className="min-w-0 truncate text-[14px] font-bold">{name}</div>
          <div className="ml-auto text-[12px] font-medium text-[#bfe3dd]">{roleLabel}</div>
        </>
      )}
    </button>
  );
}

export function PropertySidebarNav({
  tabItems,
  activeTab,
  onTabChange,
  propertyId,
  propertyName,
  propertiesHref = "/owner/properties",
  roleLabel = "Owner",
  mobileOpen = false,
  onMobileOpenChange,
}: PropertySidebarNavProps) {
  const navigate = useNavigate();
  const { properties, canCreateProperties, showSwitcher } = useAccessibleProperties();
  const [listOpen, setListOpen] = useState(false);
  const scale = useSidebarScale();
  const setMobileOpen = (open: boolean) => onMobileOpenChange?.(open);

  useEffect(() => {
    if (!mobileOpen) setListOpen(false);
  }, [mobileOpen]);

  const goProperties = () => {
    setMobileOpen(false);
    navigate(propertiesHref);
  };

  return (
    <>
      <aside
        className="fixed z-30 hidden w-[260px] flex-col rounded-[28px] bg-[#15776d] pb-[18px] pl-[18px] pt-[26px] text-white lg:flex"
        style={{ zoom: scale, top: 16 / scale, left: 16 / scale, height: `calc((100vh - 32px) / ${scale})` }}
      >
        <div className="px-0 pl-2 pr-[22px]">
          <Brand />
        </div>
        <div className="my-[22px] ml-2 mr-[26px] mt-[22px] h-px bg-white/[0.16]" />
        <div className="flex flex-col gap-[5px] pl-2 pr-[22px]">
          <div className="truncate text-[16px] font-bold tracking-[-0.2px]">{propertyName}</div>
          <button
            type="button"
            onClick={goProperties}
            className="flex items-center gap-1.5 text-left text-[13px] font-medium text-[#bfe3dd] hover:text-white"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            All properties
          </button>
        </div>
        <div className="pb-2.5 pl-2 pt-6 text-[11px] font-bold tracking-[1.2px] text-[#9fd3cb]">PROPERTY</div>
        <div className="min-h-0 flex-1">
          <SlidingNav tabItems={tabItems} activeTab={activeTab} onTabChange={onTabChange} size="desktop" />
        </div>
        <div className="mr-[18px] mt-3">
          <UserChip size="desktop" roleLabel={roleLabel} />
        </div>
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          hideClose
          className="flex w-[308px] max-w-[308px] flex-col rounded-r-[34px] border-0 bg-[#15776d] p-0 pb-0 text-white shadow-[12px_0_40px_rgba(15,23,32,0.18)] sm:max-w-[308px]"
        >
          <div className="flex items-center gap-[11px] pb-0 pl-[22px] pr-[18px] pt-[22px]">
            <div className="flex-1">
              <Brand size="phone" />
            </div>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.12]"
              aria-label="Close"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={2.2} />
            </button>
          </div>
          <div className="mx-[22px] my-4 mt-[18px] h-px bg-white/[0.16]" />
          <div className="flex flex-col gap-1 px-[22px]">
            <button
              type="button"
              className="flex min-h-8 items-center gap-2 text-left"
              onClick={() => showSwitcher && setListOpen((v) => !v)}
            >
              <div className="min-w-0 flex-1 truncate text-[17px] font-bold tracking-[-0.2px]">{propertyName}</div>
              {showSwitcher ? (
                <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform", listOpen && "rotate-180")} />
              ) : null}
            </button>
            {showSwitcher && listOpen ? (
              <div className="flex max-h-[220px] flex-col gap-0.5 overflow-y-auto rounded-2xl bg-[#1f8a7f] p-1.5">
                {properties.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      navigate(`/properties/${p.id}`);
                    }}
                    className={cn(
                      "flex min-h-11 items-center gap-2.5 rounded-[11px] px-2.5 text-left",
                      p.id === propertyId ? "bg-white/[0.12]" : "hover:bg-white/[0.08]",
                    )}
                  >
                    <div className="min-w-0 flex-1 truncate text-[14.5px] font-bold">{p.name}</div>
                    {p.id === propertyId ? <Check className="h-4 w-4 shrink-0" /> : null}
                  </button>
                ))}
                {canCreateProperties ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      navigate("/properties/new");
                    }}
                    className="flex min-h-11 items-center gap-2.5 rounded-[11px] px-2.5 text-[14px] font-bold text-[#cdeae5]"
                  >
                    <Plus className="h-4 w-4" />
                    Add property
                  </button>
                ) : null}
              </div>
            ) : null}
            <button
              type="button"
              onClick={goProperties}
              className="flex min-h-8 items-center gap-1.5 text-left text-[13px] font-medium text-[#bfe3dd]"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              All properties
            </button>
          </div>
          <div className="px-[22px] pb-2 pt-5 text-[11px] font-bold tracking-[1.2px] text-[#9fd3cb]">PROPERTY</div>
          <div className="mx-3.5 min-h-0 flex-1 overflow-y-auto">
            <SlidingNav
              tabItems={tabItems}
              activeTab={activeTab}
              onTabChange={(v) => {
                onTabChange(v);
                window.setTimeout(() => setMobileOpen(false), 260);
              }}
              size="phone"
            />
          </div>
          <div className="mx-3.5 mb-[26px] mt-3">
            <UserChip size="phone" roleLabel={roleLabel} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/** Header: breadcrumb + page title on the left; switcher, bell, avatar on the right. */
export function PropertyShellHeader({
  propertyName,
  title,
  onOpenMenu,
  actions,
}: {
  propertyName: string;
  title: string;
  onOpenMenu: () => void;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-5 border-b-[1.5px] border-[#e2e9ee] pb-[22px] pt-3 max-lg:gap-3 max-lg:border-b-0 max-lg:pb-4 max-lg:pt-2">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={onOpenMenu}
          aria-label="Open property menu"
          className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-[15px] bg-[#15776d] text-white lg:hidden"
        >
          <Menu className="h-5 w-5" strokeWidth={2} />
        </button>
        <div className="flex min-w-0 flex-col gap-[3px] max-lg:gap-px">
          <div className="truncate text-[12px] font-bold uppercase tracking-[0.8px] text-[#8494a2] max-lg:text-[11px]">
            {propertyName}
          </div>
          <div className="truncate text-[30px] font-medium leading-[1.1] tracking-[-0.6px] text-[#0f1720] max-lg:text-[23px] max-lg:leading-[1.15] max-lg:tracking-[-0.5px]">
            {title}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3.5 max-lg:gap-2">
        {actions}
        <div className="hidden lg:block">
          <PropertySwitcher variant="shell" />
        </div>
        <NotificationBell variant="shell" />
        <div className="hidden lg:block">
          <ProfileDropdown variant="shell" />
        </div>
      </div>
    </div>
  );
}
